import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Trash2, UserPlus, Info, X } from "lucide-react";
import "./ConfirmModal.scss";

/**
 * ConfirmModal — Custom confirmation dialog with modern UX.
 * Supports light/dark theme via CSS variables. No Bootstrap dependency.
 *
 * Props:
 *   show         — visibility flag
 *   onHide       — close handler
 *   onConfirm    — confirm action
 *   title        — modal title
 *   message      — body text (string or ReactNode)
 *   confirmText  — confirm button label (default "Confirm")
 *   cancelText   — cancel button label  (default "Cancel")
 *   variant      — "danger" | "warning" | "success" | "info" (default "danger")
 *   icon         — custom icon element (auto-selected from variant if omitted)
 *   loading      — disable buttons while loading
 *   confirmDisabled — extra disabled flag for confirm
 */

const VARIANT_ICONS = {
  danger: <Trash2 size={22} />,
  warning: <AlertTriangle size={22} />,
  success: <UserPlus size={22} />,
  info: <Info size={22} />,
};

const ConfirmModal = ({
  show,
  onHide,
  onConfirm,
  title = "Are you sure?",
  message = "This action cannot be undone.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  icon,
  loading = false,
  confirmDisabled = false,
}) => {
  // Close on Escape
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && !loading) onHide();
    },
    [onHide, loading]
  );

  useEffect(() => {
    if (show) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [show, handleKeyDown]);

  if (!show) return null;

  const displayIcon = icon || VARIANT_ICONS[variant] || VARIANT_ICONS.danger;

  return createPortal(
    <div className="confirm-overlay" onClick={!loading ? onHide : undefined}>
      <div
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-msg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          className="confirm-dialog__close"
          onClick={onHide}
          disabled={loading}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Icon */}
        <div className={`confirm-dialog__icon confirm-dialog__icon--${variant}`}>
          {displayIcon}
        </div>

        {/* Content */}
        <h3 className="confirm-dialog__title" id="confirm-dialog-title">
          {title}
        </h3>
        <div className="confirm-dialog__message" id="confirm-dialog-msg">
          {message}
        </div>

        {/* Actions */}
        <div className="confirm-dialog__actions">
          <button
            className="confirm-dialog__btn confirm-dialog__btn--cancel"
            onClick={onHide}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            className={`confirm-dialog__btn confirm-dialog__btn--${variant}`}
            onClick={onConfirm}
            disabled={loading || confirmDisabled}
          >
            {loading ? (
              <span className="confirm-dialog__spinner" />
            ) : null}
            {loading ? "Processing…" : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmModal;
